import React, { useState } from 'react';
import { AcceptedWrapper, VerificationIsssuesList } from '../../pages/verification/Verification.styles';
import { prettifyDate, prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import useGetVerificationData from '../../api/useGetVerificationData';
import { addDateDays, subtractDateDays } from 'odfjell-date-time-utils/dateTimeUtils';
import { ListButton, TextButton } from '../../styles/button.style';
import { useVerificationStore } from '../../context/VerificationContext';
import { ReportMetadata } from '../../types/ViewModel/Verification/ReportMetadata';
import { ImportVesselPositionsResponse } from '../../types/ViewModel/Verification/ImportVesselPositionsResponse';
import { InternalVerificationIssue } from '../../types/ViewModel/Verification/InternalVerificationIssue';
import { ReportMetadataBM } from '../../types/BusinessModel/Verification/ReportMetadataBM';
import { createReportMetadata } from '../../factory/Verification/ReportMetadataFactory';
import usePostUpdateVerificationData from '../../api/usePostUpdateVerificationData';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DefaultFlexEndWrapper } from 'src/components/ui/odfjellPanel/OdfjellPanel.styles';

type Props = {
    selectedReportMetadata: ReportMetadata;
};

const InternalVerificationIssuesComponent: React.FC<Props> = (props) => {
    const { importMissingVesselPositions } = useGetVerificationData();
    const { selectedReportMetadata, populatedReportMetadataList, setPopulatedReportMetadataList, setSelectedReportMetadata } = useVerificationStore();
    const [isNavtorTimeoutActive, setIsNavtorTimeoutActive] = useState<boolean | null>(null);
    const [importVesselPositionsResponse, setImportVesselPositionsResponse] = useState<ImportVesselPositionsResponse | null>(null);
    const { acceptInternalIssue } = usePostUpdateVerificationData();

    const onImportMissingVesselPositionsClick = async () => {
        const missingPostionsTimeStamps = selectedReportMetadata.missingPositionsIssues.map((r) => r.issueDateTimeUTCTo);
        const timeStamps = missingPostionsTimeStamps.sort((a, b) => (new Date(a) > new Date(b) ? 1 : -1));

        if (timeStamps.length === 0) return;

        const min = timeStamps[0];
        const max = timeStamps[timeStamps.length - 1];

        const from = subtractDateDays(min, 1);
        const to = addDateDays(max, 1);

        activateNavtorTimeout();

        const _importVesselPositionsResponse = await importMissingVesselPositions(selectedReportMetadata.imoNumber, from, to);
        setImportVesselPositionsResponse(_importVesselPositionsResponse);
    };

    const activateNavtorTimeout = () => {
        setIsNavtorTimeoutActive(true);
        setTimeout(() => {
            setIsNavtorTimeoutActive(false);
        }, 60000);
    };

    const onAcceptInternalIssue = async (internalVerificationIssue: InternalVerificationIssue) => {
        if (internalVerificationIssue) {
            const isToBeAccepted = internalVerificationIssue.accepted === true ? false : true;

            const _updateResponse = acceptInternalIssue(internalVerificationIssue.issueId, isToBeAccepted);
            _updateResponse.then((result) => {
                if (result) {
                    const reportMetadataBM = result as ReportMetadataBM;
                    const _updatedReportMetadata = reportMetadataBM ? createReportMetadata(reportMetadataBM) : null;

                    const _populatedReportMetadataList = populatedReportMetadataList;
                    const _reportMetadataIndex = _populatedReportMetadataList.findIndex((r) => r.reportId === _updatedReportMetadata.reportId);
                    _populatedReportMetadataList[_reportMetadataIndex].status = _updatedReportMetadata.status;
                    _populatedReportMetadataList[_reportMetadataIndex].acceptedAt = _updatedReportMetadata.acceptedAt;
                    _populatedReportMetadataList[_reportMetadataIndex].acceptedByUser = _updatedReportMetadata.acceptedByUser;
                    _populatedReportMetadataList[_reportMetadataIndex].missingAolIssues = _updatedReportMetadata.missingAolIssues;
                    _populatedReportMetadataList[_reportMetadataIndex].missingPositionsIssues = _updatedReportMetadata.missingPositionsIssues;
                    _populatedReportMetadataList[_reportMetadataIndex].timeInconsistencyIssues = _updatedReportMetadata.timeInconsistencyIssues;
                    _populatedReportMetadataList[_reportMetadataIndex].reportFormatIssues = _updatedReportMetadata.reportFormatIssues;

                    setPopulatedReportMetadataList([..._populatedReportMetadataList]);
                    setSelectedReportMetadata({ ..._populatedReportMetadataList[_reportMetadataIndex] });
                }
            });
        }
    };

    const getAcceptedTooltip = (r: any) => {
        return 'Accepted ' + prettifyDate(r.acceptedAt) + ' by ' + r.acceptedByUser;
    };

    return (
        <>
            {props.selectedReportMetadata.missingAolIssues && props.selectedReportMetadata.missingAolIssues.length > 0 && (
                <VerificationIsssuesList>
                    <li key={'dnv_list_item_1'}>
                        <section className="header-section">
                            <div className="row issues">
                                <span>Missing AOLs</span>

                                <span className={'issue'}>Internal issue</span>
                            </div>
                        </section>
                        <section className="desc-section">
                            <div className="row row-desc">
                                <span className="label">The following AOLs contains issues</span>
                            </div>
                            {props.selectedReportMetadata.missingAolIssues.length > 0 &&
                                props.selectedReportMetadata.missingAolIssues.map((v, i) => {
                                    return (
                                        <div className="row row-2" key={'list_item_1_1_' + v.issueId}>
                                            <span>{i + 1}.</span>
                                            <label>From</label>
                                            <span>{prettifyDateTimeHHmm(v.issueDateTimeLocalFrom)}</span>
                                            <label>To</label>
                                            <span>{prettifyDateTimeHHmm(v.issueDateTimeLocalTo)}</span>
                                            {!v.acceptedAt && (
                                                <TextButton className="inline" onClick={() => onAcceptInternalIssue(v)}>
                                                    Accept issue
                                                </TextButton>
                                            )}
                                            {v.acceptedAt && (
                                                <AcceptedWrapper title={getAcceptedTooltip(v)}>
                                                    <FontAwesomeIcon className="info-icon" icon="info-circle" />
                                                    <TextButton className="inline" onClick={() => onAcceptInternalIssue(v)}>
                                                        Undo accept
                                                    </TextButton>
                                                </AcceptedWrapper>
                                            )}
                                        </div>
                                    );
                                })}
                        </section>
                    </li>
                </VerificationIsssuesList>
            )}
            {props.selectedReportMetadata.missingPositionsIssues && props.selectedReportMetadata.missingPositionsIssues.length > 0 && (
                <VerificationIsssuesList>
                    <li key={'dnv_list_item_2'}>
                        <section className="header-section">
                            <div className="row issues">
                                <span>Missing positions</span>
                                <span className={'issue'}>Internal issue</span>
                            </div>
                        </section>
                        <section className="desc-section">
                            <div className="missing-row">
                                <h4>There are missing positions for the following dates</h4>

                                {props.selectedReportMetadata.missingPositionsIssues.map((v) => {
                                    return (
                                        <div className="row" key={'list_item_1_2_' + v.issueId}>
                                            {prettifyDate(v.issueDateTimeUTCTo)}
                                            {!v.acceptedAt && (
                                                <TextButton className="inline" onClick={() => onAcceptInternalIssue(v)}>
                                                    Accept issue
                                                </TextButton>
                                            )}
                                            {v.acceptedAt && (
                                                <AcceptedWrapper title={getAcceptedTooltip(v)}>
                                                    <FontAwesomeIcon className="info-icon" icon="info-circle" />
                                                    <TextButton className="inline" onClick={() => onAcceptInternalIssue(v)}>
                                                        Undo accept issue
                                                    </TextButton>
                                                </AcceptedWrapper>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                            <div className="row missing-button">
                                <ListButton disabled={isNavtorTimeoutActive} onClick={onImportMissingVesselPositionsClick}>
                                    Get positions from Navtor
                                </ListButton>
                                {importVesselPositionsResponse &&
                                    importVesselPositionsResponse.response &&
                                    importVesselPositionsResponse.numberOfPositionsRetrieved === 0 && (
                                        <span className="warning-msg">
                                            No positions retrieved from Navtor. This internal issue will have to be accepted.
                                        </span>
                                    )}
                                {importVesselPositionsResponse &&
                                    importVesselPositionsResponse.response &&
                                    importVesselPositionsResponse.numberOfPositionsRetrieved > 0 && (
                                        <span className="warning-msg">
                                            {importVesselPositionsResponse.numberOfPositionsRetrieved} positions were updated.
                                        </span>
                                    )}
                                {importVesselPositionsResponse && !importVesselPositionsResponse.response && (
                                    <span className="warning-msg">No positions were updated.</span>
                                )}
                            </div>
                            <div className="row missing-button">
                                {isNavtorTimeoutActive && (
                                    <span className="warning-msg">
                                        New positions have been requested to Navtor. Please verify in 1 day if a new report has been generated or
                                        contact DevOps.
                                    </span>
                                )}
                            </div>
                        </section>
                    </li>
                </VerificationIsssuesList>
            )}
            {props.selectedReportMetadata.timeInconsistencyIssues && props.selectedReportMetadata.timeInconsistencyIssues.length > 0 && (
                <VerificationIsssuesList>
                    <li key={'dnv_list_item_3'}>
                        <section className="header-section">
                            <div className="row issues">
                                <span>Time inconsistency</span>
                                <span className={'issue'}>Internal issue</span>
                            </div>
                            <div className="missing-row">
                                <h4>
                                    Time inconsistencies have been detected, based on absolute difference between (Date/Time UTC - previous Date/Time
                                    UTC) and Time_Since_Previous_Report.
                                </h4>

                                {props.selectedReportMetadata.timeInconsistencyIssues.map((v) => {
                                    return (
                                        <li key={'list_item_1_3_' + v.issueId}>
                                            <DefaultFlexEndWrapper>
                                                {!v.acceptedAt && (
                                                    <TextButton className="inline" onClick={() => onAcceptInternalIssue(v)}>
                                                        Accept issue
                                                    </TextButton>
                                                )}
                                                {v.acceptedAt && (
                                                    <AcceptedWrapper title={getAcceptedTooltip(v)}>
                                                        <FontAwesomeIcon className="info-icon" icon="info-circle" />
                                                        <TextButton className="inline" onClick={() => onAcceptInternalIssue(v)}>
                                                            Undo accept issue
                                                        </TextButton>
                                                    </AcceptedWrapper>
                                                )}
                                            </DefaultFlexEndWrapper>

                                            <ul className="missing-item">
                                                <label>UTC time period: </label>
                                                {prettifyDateTimeHHmm(v.issueDateTimeUTCFrom)} - {prettifyDateTimeHHmm(v.issueDateTimeUTCTo)}
                                            </ul>
                                            <ul className="missing-item">{v.details}</ul>
                                        </li>
                                    );
                                })}
                            </div>
                        </section>
                    </li>
                </VerificationIsssuesList>
            )}
            {props.selectedReportMetadata.reportFormatIssues && props.selectedReportMetadata.reportFormatIssues.length > 0 && (
                <VerificationIsssuesList>
                    <li key={'dnv_list_item_4'}>
                        <section className="header-section">
                            <div className="row issues">
                                <span>Report Format Issue</span>
                                <span className={'issue'}>Internal issue</span>
                            </div>
                            <div className="missing-row">
                                <h4>The DNV API flagged report format issues when the report was sent.</h4>

                                {props.selectedReportMetadata.reportFormatIssues.map((v) => {
                                    return (
                                        <li key={'list_item_1_4_' + v.issueId}>
                                            <ul className="missing-item">
                                                <label>UTC time period: </label>
                                                {prettifyDateTimeHHmm(v.issueDateTimeUTCFrom)} - {prettifyDateTimeHHmm(v.issueDateTimeUTCTo)}
                                            </ul>
                                            <ul className="missing-item">{v.details}</ul>
                                        </li>
                                    );
                                })}
                            </div>
                        </section>
                    </li>
                </VerificationIsssuesList>
            )}
        </>
    );
};
export default InternalVerificationIssuesComponent;
